import {
  fetchAdminSeasonTiers,
  fetchAdminSparksRedemptions,
  fetchAdminSparksRewards,
  reviewAdminSparksRedemption,
  updateAdminSeasonTier,
  updateAdminSparksReward,
  type AdminSeasonTier,
  type AdminSparksRedemption,
  type AdminSparksReward,
} from "@/api/admin";
import { AdminCard, MetricCard } from "@/components/admin/AdminShell";
import { formatCoins } from "@/components/admin/adminConstants";
import { Button } from "@/components/ui/Button";
import { useCallback, useEffect, useState } from "react";

function currentSeasonKey() {
  const d = new Date();
  const ist = new Date(d.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  const y = ist.getFullYear();
  const m = String(ist.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

export function AdminRewardsTab() {
  const [rewards, setRewards] = useState<AdminSparksReward[]>([]);
  const [redemptions, setRedemptions] = useState<AdminSparksRedemption[]>([]);
  const [tiers, setTiers] = useState<AdminSeasonTier[]>([]);
  const [seasonKey, setSeasonKey] = useState(currentSeasonKey());
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setError(null);
      const [r, p, t] = await Promise.all([
        fetchAdminSparksRewards(),
        fetchAdminSparksRedemptions("pending", 40),
        fetchAdminSeasonTiers(seasonKey),
      ]);
      setRewards(r);
      setRedemptions(p);
      setTiers(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load rewards");
    }
  }, [seasonKey]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function saveReward(reward: AdminSparksReward) {
    setBusyId(reward.id);
    try {
      await updateAdminSparksReward(reward.id, {
        title: reward.title,
        description: reward.description,
        cost_sparks: reward.cost_sparks,
        active: reward.active,
        sort_order: reward.sort_order,
      });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusyId(null);
    }
  }

  async function saveTier(tier: AdminSeasonTier) {
    const key = `${tier.season_key}-${tier.tier}`;
    setBusyId(key);
    try {
      await updateAdminSeasonTier(tier.season_key, tier.tier, {
        rank_from: tier.rank_from,
        rank_to: tier.rank_to,
        sparks_reward: tier.sparks_reward,
      });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-6">
      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      <div className="grid gap-4 sm:grid-cols-3">
        <MetricCard label="Reward catalog" value={String(rewards.length)} sub="Sparks store items" />
        <MetricCard label="Pending redemptions" value={String(redemptions.length)} sub="Needs fulfillment" />
        <MetricCard label="Season tiers" value={String(tiers.length)} sub={`Season ${seasonKey}`} />
      </div>

      <AdminCard title="Pending Sparks redemptions">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs text-ink-muted">
              <tr>
                <th className="px-2 py-2">User</th>
                <th className="px-2 py-2">Reward</th>
                <th className="px-2 py-2">Sparks</th>
                <th className="px-2 py-2">When</th>
                <th className="px-2 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {redemptions.map((r) => (
                <tr key={r.id} className="border-t border-black/[0.04]">
                  <td className="px-2 py-2">@{r.username ?? "—"}</td>
                  <td className="px-2 py-2">{r.reward_id}</td>
                  <td className="px-2 py-2 tabular-nums">{r.sparks_spent}</td>
                  <td className="px-2 py-2 text-xs text-ink-muted">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex gap-1">
                      <Button
                        disabled={busyId === r.id}
                        onClick={() =>
                          void (async () => {
                            setBusyId(r.id);
                            try {
                              await reviewAdminSparksRedemption(r.id, "fulfill");
                              await reload();
                            } finally {
                              setBusyId(null);
                            }
                          })()
                        }
                      >
                        Fulfill
                      </Button>
                      <Button
                        variant="outline"
                        disabled={busyId === r.id}
                        onClick={() =>
                          void (async () => {
                            setBusyId(r.id);
                            try {
                              await reviewAdminSparksRedemption(r.id, "cancel");
                              await reload();
                            } finally {
                              setBusyId(null);
                            }
                          })()
                        }
                      >
                        Cancel
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!redemptions.length ? (
                <tr>
                  <td colSpan={5} className="px-2 py-6 text-center text-ink-muted">
                    No pending redemptions
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </AdminCard>

      <AdminCard title="Sparks reward catalog">
        <div className="space-y-3">
          {rewards.map((reward) => (
            <div
              key={reward.id}
              className="grid gap-2 rounded-xl border border-black/[0.06] bg-[#f8f9fb] p-3 md:grid-cols-2 lg:grid-cols-6 lg:items-end"
            >
              <label className="text-xs lg:col-span-2">
                Title
                <input
                  className="mt-1 w-full rounded border border-black/10 bg-white px-2 py-1 text-sm"
                  value={reward.title}
                  onChange={(e) =>
                    setRewards((rows) =>
                      rows.map((r) => (r.id === reward.id ? { ...r, title: e.target.value } : r)),
                    )
                  }
                />
              </label>
              <label className="text-xs lg:col-span-2">
                Description
                <input
                  className="mt-1 w-full rounded border border-black/10 bg-white px-2 py-1 text-sm"
                  value={reward.description ?? ""}
                  onChange={(e) =>
                    setRewards((rows) =>
                      rows.map((r) =>
                        r.id === reward.id ? { ...r, description: e.target.value } : r,
                      ),
                    )
                  }
                />
              </label>
              <label className="text-xs">
                Cost (sparks)
                <input
                  type="number"
                  className="mt-1 w-full rounded border border-black/10 bg-white px-2 py-1 text-sm"
                  value={reward.cost_sparks}
                  onChange={(e) =>
                    setRewards((rows) =>
                      rows.map((r) =>
                        r.id === reward.id
                          ? { ...r, cost_sparks: Number(e.target.value) || 0 }
                          : r,
                      ),
                    )
                  }
                />
              </label>
              <label className="text-xs">
                Sort
                <input
                  type="number"
                  className="mt-1 w-full rounded border border-black/10 bg-white px-2 py-1 text-sm"
                  value={reward.sort_order}
                  onChange={(e) =>
                    setRewards((rows) =>
                      rows.map((r) =>
                        r.id === reward.id
                          ? { ...r, sort_order: Number(e.target.value) || 0 }
                          : r,
                      ),
                    )
                  }
                />
              </label>
              <div className="flex items-center gap-2 lg:col-span-6">
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={reward.active}
                    onChange={(e) =>
                      setRewards((rows) =>
                        rows.map((r) =>
                          r.id === reward.id ? { ...r, active: e.target.checked } : r,
                        ),
                      )
                    }
                  />
                  Active
                </label>
                <span className="text-xs text-ink-muted">
                  {reward.id} · {reward.category} · {reward.redemption_count ?? 0} redemptions
                </span>
                <Button
                  className="ml-auto"
                  disabled={busyId === reward.id}
                  onClick={() => void saveReward(reward)}
                >
                  Save
                </Button>
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      <AdminCard
        title="Challenge season tiers"
        action={
          <input
            className="rounded-lg border border-black/10 px-2 py-1 text-xs"
            value={seasonKey}
            onChange={(e) => setSeasonKey(e.target.value)}
            placeholder="YYYY-MM"
          />
        }
      >
        <p className="mb-3 text-xs text-ink-muted">
          Monthly rank tiers and Sparks payout amounts (creator challenge leaderboard).
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs text-ink-muted">
              <tr>
                <th className="px-2 py-2">Tier</th>
                <th className="px-2 py-2">Rank from</th>
                <th className="px-2 py-2">Rank to</th>
                <th className="px-2 py-2">Sparks reward</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier) => (
                <tr key={`${tier.season_key}-${tier.tier}`} className="border-t border-black/[0.04]">
                  <td className="px-2 py-2 font-medium">Tier {tier.tier}</td>
                  <td className="px-2 py-2">
                    <input
                      type="number"
                      className="w-20 rounded border border-black/10 bg-white px-2 py-1 text-sm"
                      value={tier.rank_from}
                      onChange={(e) =>
                        setTiers((rows) =>
                          rows.map((t) =>
                            t.tier === tier.tier
                              ? { ...t, rank_from: Number(e.target.value) || 0 }
                              : t,
                          ),
                        )
                      }
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="number"
                      className="w-20 rounded border border-black/10 bg-white px-2 py-1 text-sm"
                      value={tier.rank_to}
                      onChange={(e) =>
                        setTiers((rows) =>
                          rows.map((t) =>
                            t.tier === tier.tier
                              ? { ...t, rank_to: Number(e.target.value) || 0 }
                              : t,
                          ),
                        )
                      }
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="number"
                      className="w-28 rounded border border-black/10 bg-white px-2 py-1 text-sm"
                      value={tier.sparks_reward}
                      onChange={(e) =>
                        setTiers((rows) =>
                          rows.map((t) =>
                            t.tier === tier.tier
                              ? { ...t, sparks_reward: Number(e.target.value) || 0 }
                              : t,
                          ),
                        )
                      }
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Button
                      variant="outline"
                      disabled={busyId === `${tier.season_key}-${tier.tier}`}
                      onClick={() => void saveTier(tier)}
                    >
                      Save
                    </Button>
                  </td>
                </tr>
              ))}
              {!tiers.length ? (
                <tr>
                  <td colSpan={5} className="px-2 py-6 text-center text-ink-muted">
                    No tiers for season {seasonKey}. Add rows in DB or pick another month.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {tiers.length ? (
          <p className="mt-2 text-xs text-ink-muted">
            Example payout range: tier 1 = {formatCoins(tiers[0]?.sparks_reward)} sparks for ranks{" "}
            {tiers[0]?.rank_from}–{tiers[0]?.rank_to}
          </p>
        ) : null}
      </AdminCard>
    </div>
  );
}
